import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Github, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import orbitLogo from '/images/logo.png';
import orbitDarkLogo from '/images/logo-dark.png';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { ForgotPassword } from '@/components/ForgotPassword';
import { useAuth } from '../contexts/AuthContext';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import { Footer } from '@/components/Footer';
import { MiniFooter } from '@/components/MiniFooter';
import DashboardCarousel from '@/components/landingpage/DashboardCarousel';

interface LoginProps {
  onSwitchToSignup: () => void;
  onNavigate?: (route: string) => void;
}

export function Login({ onSwitchToSignup, onNavigate }: LoginProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [emailid, setEmailid] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const { login, githubLogin } = useAuth();
  const { theme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login({ emailid, password: password });
      if (onNavigate) {
        onNavigate('dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error instanceof Error && error.message ? error.message : 'Login failed. Please check your credentials.');
    }
    setIsLoading(false);
  };

  const handleGithubLogin = async () => {
    setIsLoading(true);
    try {
      await githubLogin();
    } catch (error) {
      toast.error('GitHub login failed. Please try again.');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary/80 items-center justify-center p-12">
        <div className="w-full max-w-2xl text-center">
          <div className="mb-8">
            <DashboardCarousel
              images={["/images/landing-1.png", "/images/landing-2.png", "/images/landing-3.png"]}
            />
          </div>
          {/* <h2 className="text-3xl font-bold text-white mb-4">
            Secure Your Code
          </h2> */}
          <p className="text-blue-100 text-lg leading-relaxed">
            Advanced scanning and vulnerability management for your development workflow.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-4">
            <Button
              asChild
              variant="ghost"
              size="icon"
              aria-label="Back to home page"
              title="Back to home"
              className="size-9 text-muted-foreground hover:text-foreground"
            >
              <Link to="/">
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="text-center mb-8">
            <Link
              to="/"
              aria-label="ORbiT8 home"
              className="inline-block transition-opacity hover:opacity-80"
            >
              <img
                src={theme === 'dark' ? orbitDarkLogo : orbitLogo}
                alt="ORbiT8"
                className="h-12 w-auto mx-auto mb-4"
              />
            </Link>
           </div>

          <Card className="bg-card shadow-lg rounded-lg border">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-xl text-center">Sign In</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button
                onClick={handleGithubLogin}
                disabled={isLoading}
                variant="outline"
                className="w-full border-border hover:bg-accent transition-colors"
              >
                <Github className="w-5 h-5 mr-2" />
                Continue with GitHub
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="emailid">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      id="emailid"
                      type="email"
                      placeholder="Enter your email"
                      value={emailid}
                      onChange={(e) => setEmailid(e.target.value)}
                      className="pl-10"
                      maxLength={320}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      maxLength={128}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      id="remember"
                      type="checkbox"
                      className="rounded border-border text-primary focus:ring-primary/20"
                    />
                    <Label htmlFor="remember" className="text-sm text-muted-foreground">
                      Remember me
                    </Label>
                  </div>
                  <Button
                    type='button'
                    variant="link"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-primary hover:text-primary/80 p-0"
                  >
                    Forgot password?
                  </Button>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full shadow-lg shadow-primary/20"
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>

              <div className="text-center">
                <span className="text-muted-foreground">Don't have an account? </span>
                <Button
                  variant="link"
                  onClick={onSwitchToSignup}
                  className="text-primary hover:text-primary/80 p-0"
                >
                  Sign up
                </Button>
              </div>

             
            </CardContent>
          </Card>
         <MiniFooter/>
        </div>
       
      </div>

      <ForgotPassword
        isOpen={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />

       <Toaster />
     
    </div>
  );
}